angular.module('bizmetryApp').factory('DatasourceService', function($http, CONFIG) {

  // Agents older than the driver-agnostic normalization in bizmetry-remote-agent
  // (PullDataService#normalizeValue) leak JDBC driver wrappers into pulled rows,
  // e.g. PostgreSQL PGobject: {"type":"jsonb","value":"<escaped json>","null":false}.
  // Unwrap them here, once, so every screen receives plain values / real JSON.
  function unwrapDriverValue(v) {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return v;
    var keys = Object.keys(v);
    var isWrapper = keys.length === 3 && typeof v.type === 'string'
      && 'value' in v && typeof v['null'] === 'boolean';
    if (!isWrapper) return v;
    if (v['null'] || v.value === null || v.value === undefined) return null;
    if (/^jsonb?$/i.test(v.type) && typeof v.value === 'string') {
      try { return JSON.parse(v.value); } catch (e) { return v.value; }
    }
    return v.value;
  }

  function normalizePulledRows(data) {
    if (data && Array.isArray(data.rows)) {
      data.rows.forEach(function (row) {
        if (!row || typeof row !== 'object') return;
        Object.keys(row).forEach(function (k) { row[k] = unwrapDriverValue(row[k]); });
      });
    }
    return data;
  }

  return {

    getDatasources: function(accountId) {
      return $http({
        method: 'GET',
        url: CONFIG.BIZMETRY_BACKEND_URL + '/v1/api/bizmetry/datasource?isEnabled=true',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Account-Id': accountId
        }
      }).then(response => response.data);
    },

    // ============================================
    // CREATE / UPDATE
    // ============================================

    /**
     * Creates or updates a datasource.
     *
     * @param {Object} datasource  - Full datasource object from scope
     * @param {string} accountId   - Current user/account ID (used in headers)
     * @param {boolean} isEditMode - true → PATCH, false → POST
     */
    saveDatasource: function(datasource, accountId, isEditMode) {
      const url = isEditMode
        ? `${CONFIG.BIZMETRY_BACKEND_URL}/v1/api/bizmetry/datasource/${datasource.id}`
        : `${CONFIG.BIZMETRY_BACKEND_URL}/v1/api/bizmetry/datasource`;

      const payload = {
        datasourceName:        datasource.datasourceName,
        datasourceDescription: datasource.datasourceDescription,
        datasourceType:        datasource.datasourceType,
        datasourceHost:        datasource.datasourceHost,
        datasourcePort:        datasource.datasourcePort,
        datasourceUser:        datasource.datasourceUser,
        datasourcePassword:    datasource.datasourcePassword,
        datasourceDbName:      datasource.datasourceDbName,
        agentId:               datasource.agentId,
        agentName:             datasource.agentName,
        environmentId:         datasource.environmentId,
        environmentName:       datasource.environmentName
      };

      return $http({
        method: isEditMode ? 'PATCH' : 'POST',
        url,
        data: payload,
        headers: {
          'Content-Type': 'application/json',
          'X-User-Account-Id': accountId,
          'X-User-Id':         accountId
        }
      }).then(response => response.data);
    },

    // ============================================
    // SCHEMAS / ENTITIES / ATTRIBUTES
    // ============================================

    getSchemas: function(datasourceId) {
      return $http({
        method: 'GET',
        url: CONFIG.BIZMETRY_BACKEND_URL + '/v1/api/bizmetry-agents/agents/datasource/' + datasourceId + '/schemas'
      }).then(response => response.data);
    },

    getEntities: function(datasourceId, schemaName) {
      return $http({
        method: 'GET',
        url: CONFIG.BIZMETRY_BACKEND_URL + '/v1/api/bizmetry-agents/agents/datasource/' + datasourceId + '/schemas/' + encodeURIComponent(schemaName) + '/entities'
      }).then(response => response.data);
    },

    getAttributes: function(datasourceId, schemaName, tableName) {
      return $http({
        method: 'GET',
        url: CONFIG.BIZMETRY_BACKEND_URL + '/v1/api/bizmetry-agents/agents/datasource/' + datasourceId + '/schemas/' + encodeURIComponent(schemaName) + '/entities/' + encodeURIComponent(tableName) + '/attributes'
      }).then(response => response.data);
    },

    // ============================================
    // TABLE DATA
    // ============================================

    /**
     * Fetches rows from a table with pagination, ordering and optional ID search.
     *
     * @param {string} datasourceId
     * @param {string} schemaName
     * @param {string} tableName
     * @param {Array}  columns        - Array of {sequence, columnName}
     * @param {Object} options
     *   - limit {number}
     *   - offset {number}
     *   - orderBy {string}
     *   - orderDirection {string}  'ASC' | 'DESC'
     *   - idColumnName {string}    column to filter by ID
     *   - idValue {string}         value to match
     *   - whereClause {string}     raw WHERE clause (alternative to idColumnName/idValue)
     *   - whereParams {Array}      bound params for whereClause
     *   - searchText {string}      free text, case-insensitive "contains" over searchColumns
     *   - searchColumns {Array}    column names searched (default: all requested columns)
     */
    getTableData: function(datasourceId, schemaName, tableName, columns, options) {
      const requestBody = {
        schemaName,
        tableName,
        columns: columns || []
      };

      if (options) {
        if (options.limit     != null) requestBody.limit          = options.limit;
        if (options.offset    != null) requestBody.offset         = options.offset;
        if (options.orderBy)           requestBody.orderBy        = options.orderBy;
        if (options.orderDirection)    requestBody.orderDirection = options.orderDirection;

        if (options.idColumnName && options.idValue) {
          requestBody.idColumnName = options.idColumnName;
          requestBody.idValue      = options.idValue;
        }

        if (options.searchText && options.searchText.trim()) {
          requestBody.searchText = options.searchText.trim();
          if (Array.isArray(options.searchColumns)) {
            requestBody.searchColumns = options.searchColumns;
          }
        }

        if (options.whereClause) {
          requestBody.whereClause = options.whereClause;
          if (Array.isArray(options.whereParams)) {
            requestBody.whereParams = options.whereParams;
          }
        }
      }

      return $http({
        method: 'POST',
        url: CONFIG.BIZMETRY_BACKEND_URL + '/v1/api/bizmetry-agents/agents/datasource/' + datasourceId + '/pull-data',
        data: requestBody,
        headers: { 'Content-Type': 'application/json' }
      }).then(response => normalizePulledRows(response.data));
    },

    // ============================================
    // AGENTS
    // ============================================

    testConnection: function(agentId, payload) {
      return $http({
        method: 'POST',
        url: CONFIG.BIZMETRY_BACKEND_URL + '/v1/api/bizmetry-agents/agents/' + agentId + '/test-connection',
        data: payload,
        headers: { 'Content-Type': 'application/json' }
      }).then(response => response.data);
    },

    getAgentsByAccountAndStatus: function(accountId, status) {
      const url = CONFIG.BIZMETRY_BACKEND_URL + '/v1/api/bizmetry-agents/agents/search' +
                  '?account_id=' + encodeURIComponent(accountId) +
                  (status ? '&status=' + encodeURIComponent(status) : '');

      return $http({
        method: 'GET',
        url,
        headers: { 'Content-Type': 'application/json' }
      }).then(response => response.data);
    },

    getReachableAgentsByAccount: function(accountId) {
      const url = CONFIG.BIZMETRY_BACKEND_URL + '/v1/api/bizmetry-agents/agents/search' +
                  '?account_id=' + encodeURIComponent(accountId) + '&isReachable=true';

      return $http({
        method: 'GET',
        url,
        headers: { 'Content-Type': 'application/json' }
      }).then(response => response.data);
    }

  };
});